import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  catchError,
  finalize,
  map,
  Observable,
  Observer,
  of,
  Subject,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import { TakeOwnershipRequest, ViewFlightInfoService } from './view-flight-info-service';
import { buildFriendlyError, ProductService } from './product-service';
import { AuthService } from '../../core/auth/auth-service';
import { OwnershipConfirmDialog } from '../../utilities/components/ownership-confirm-dialog/ownership-confirm-dialog';
import { getProductCode } from '../models/product-mapping';
import { OwnershipService } from './ownership-service';
import { extractFlightDetails } from './payload-mapper';

@Injectable({ providedIn: 'root' })
export class OwnershipCoordinatorService {
  private readonly refreshSubject = new Subject<void>();
  refresh$ = this.refreshSubject.asObservable();

  private readonly loadingSubject = new Subject<boolean>();
  loading$ = this.loadingSubject.asObservable();

  private cfhCompletedSubject = new Subject<void>();
  cfhCompleted$ = this.cfhCompletedSubject.asObservable();
  private refreshTriggered = false;
  notifyCfhCompleted(): void {
    this.refreshTriggered = false;
    this.cfhCompletedSubject.next();
  }

  triggerCfhReload(): void {
    this.refreshSubject.next();
  }

  private inProgress = false;

  constructor(
    private readonly dialog: MatDialog,
    private readonly viewInfoService: ViewFlightInfoService,
    private readonly productService: ProductService,
    private readonly auth: AuthService,
    private readonly ownershipService: OwnershipService
  ) {}

  initiateTakeOwnershipWithResult(
    flightHeaderFromCFH: any,
    confirmMessage: string
  ): Observable<boolean> {
    if (this.inProgress) return of(false);

    const currentUserNid = this.auth.getUserNid() ?? '';
    if (flightHeaderFromCFH?.product?.ownerId === currentUserNid) {
      return of(true);
    }
    const { carrierCode, flightNumber, payload } = this.buildTakeOwnershipRequest(
      flightHeaderFromCFH,
      currentUserNid
    );

    return new Observable<boolean>((observer) => {
      this.dialog
        .open(OwnershipConfirmDialog, {
          width: '678px',
          maxWidth: 'none',
          disableClose: true,
          data: {
            title: 'Flight Ownership',
            message: confirmMessage,

            confirmAction: () => {
              this.inProgress = true;
              this.loadingSubject.next(true);
              this.refreshTriggered = false;

              this.cfhCompletedSubject = new Subject<void>();
              this.cfhCompleted$ = this.cfhCompletedSubject.asObservable();

              const waitForCfh$ = this.cfhCompleted$.pipe(take(1));

              return this.executeTakeOwnership(carrierCode, flightNumber, payload, waitForCfh$);
            },
          },
        })
        .afterClosed()
        .subscribe({
          next: (result) => {
            observer.next(result === true);
            observer.complete();
          },
        });
    });
  }
  private executeTakeOwnership(
    carrierCode: string,
    flightNumber: string,
    payload: TakeOwnershipRequest,
    waitForCfh$: Observable<void>
  ): Observable<boolean> {
    return this.viewInfoService.takeOwnership(carrierCode, flightNumber, payload).pipe(
      switchMap((res) => {
        if (res?.data?.AcknowledgementMessage !== 'true') {
          throw new Error('Unable to take ownership.');
        }

        if (!this.refreshTriggered) {
          this.refreshTriggered = true;
          this.refreshSubject.next();
        }

        return waitForCfh$;
      }),
      map(() => true),
      catchError((err) => throwError(() => new Error(buildFriendlyError(err)))),
      finalize(() => {
        this.inProgress = false;
        this.loadingSubject.next(false);
      })
    );
  }
  initiateTakeOwnership(flightHeaderFromCFH: any, confirmMessage: string): void {
    this.initiateTakeOwnershipWithResult(flightHeaderFromCFH, confirmMessage).subscribe();
  }

  private buildTakeOwnershipRequest(
    cfh: any,
    currentUserNid: string
  ): {
    carrierCode: string;
    flightNumber: string;
    payload: TakeOwnershipRequest;
  } {
    const leg = cfh?.datedFlightLeg;
    if (!leg) throw new Error('CFH missing datedFlightLeg');

    const dt = leg?.scheduledDepartureDateLocal;

    if (!dt) throw new Error('No usable departure time found in CFH');

    const [date, time] = dt.split('T');

    const product = this.productService.getCurrentProduct();
    if (!product) throw new Error('Product not set');

    const searchPayload = this.productService.get();
    const extracted = extractFlightDetails(searchPayload?.flightNumbers ?? []);

    const payload: TakeOwnershipRequest = {
      origin: leg.originStation,
      date,
      time,
      commodity: getProductCode(product),
      currentOwner: currentUserNid,
    };

    if (extracted?.suffix) {
      payload.flightSuffix = extracted.suffix;
    }

    return {
      carrierCode: leg.operatorCarrierCode,
      flightNumber: extracted?.flightNumber ?? leg.operationalFlightNumber,
      payload,
    };
  }
  takeOwnershipWithoutConfirmation(flightHeaderFromCFH: any): Observable<boolean> {
    if (this.inProgress) {
      return of(false);
    }

    const currentUserNid = this.auth.getUserNid() ?? '';

    if (!currentUserNid) {
      return of(false);
    }

    const { carrierCode, flightNumber, payload } = this.buildTakeOwnershipRequest(
      flightHeaderFromCFH,
      currentUserNid
    );

    this.inProgress = true;
    this.loadingSubject.next(true);

    this.refreshTriggered = false;
    this.cfhCompletedSubject = new Subject<void>();
    this.cfhCompleted$ = this.cfhCompletedSubject.asObservable();

    const waitForCfh$ = this.cfhCompleted$.pipe(take(1));

    return this.viewInfoService.takeOwnership(carrierCode, flightNumber, payload).pipe(
      switchMap((res) => {
        if (res?.data?.AcknowledgementMessage !== 'true') {
          throw new Error('Unable to take ownership.');
        }

        if (!this.refreshTriggered) {
          this.refreshTriggered = true;
          this.refreshSubject.next();
        }

        return waitForCfh$;
      }),
      map(() => true),
      catchError((err) => {
        return throwError(() => err);
      }),

      finalize(() => {
        this.inProgress = false;
        this.loadingSubject.next(false);
      })
    );
  }
  private executeReleaseAndRefresh(
    carrierCode: string,
    flightNumber: string,
    payload: any
  ): Observable<boolean> {
    return this.ownershipService.releaseFromFlight(carrierCode, flightNumber, payload).pipe(
      tap(() => {
        if (!this.refreshTriggered) {
          this.refreshTriggered = true;
          this.refreshSubject.next();
        }
      }),
      map(() => true),
      catchError((err) => throwError(() => new Error(buildFriendlyError(err))))
    );
  }
  initiateRelease(
    carrierCode: string,
    flightNumber: string,
    payload: any,
    confirmMessage: string
  ): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      this.dialog
        .open(OwnershipConfirmDialog, {
          width: '581px',
          height: '170px',
          maxWidth: 'none',
          disableClose: true,
          data: {
            title: 'Release Ownership',
            message: confirmMessage,
            confirmText: 'Release',
            cancelText: 'Cancel',
            confirmAction: () => this.executeReleaseAndRefresh(carrierCode, flightNumber, payload),
          },
        })
        .afterClosed()
        .subscribe({
          next: (result) => {
            observer.next(result === true);
            observer.complete();
          },
          error: (err) => observer.error(err),
        });
    });
  }

  private buildReleaseDialogConfig(
    carrierCode: string,
    flightNumber: string,
    payload: any,
    confirmMessage: string
  ) {
    return {
      width: '581px',
      height: '170px',
      maxWidth: 'none',
      disableClose: true,
      data: {
        title: 'Release Ownership',
        message: confirmMessage,
        confirmAction: () => this.executeRelease(carrierCode, flightNumber, payload),
      },
    };
  }

  private executeRelease(
    carrierCode: string,
    flightNumber: string,
    payload: any
  ): Observable<boolean> {
    this.refreshTriggered = false;
    this.cfhCompletedSubject = new Subject<void>();
    this.cfhCompleted$ = this.cfhCompletedSubject.asObservable();

    const waitForCfh$ = this.cfhCompleted$.pipe(take(1));

    return this.ownershipService.releaseFromFlight(carrierCode, flightNumber, payload).pipe(
      switchMap(() => {
        this.triggerRefresh();
        return waitForCfh$;
      }),
      map(() => true),
      catchError((err) => throwError(() => new Error(buildFriendlyError(err))))
    );
  }

  private triggerRefresh(): void {
    if (!this.refreshTriggered) {
      this.refreshTriggered = true;
      this.refreshSubject.next();
    }
  }

  private createReleaseDialogObserver(observer: Observer<boolean>) {
    return {
      next: (result: boolean) => {
        observer.next(result === true);
        observer.complete();
      },
      error: (err: Error) => observer.error(err),
    };
  }
  releaseAllFlights(confirmMessage: string): void {
    const currentOwner = this.auth.getUserNid() ?? '';
    if (!currentOwner) return;

    this.dialog
      .open(OwnershipConfirmDialog, {
        width: '500px',
        disableClose: true,
        data: {
          title: 'Release All Flights',
          message: confirmMessage,

          confirmAction: () => {
            this.inProgress = true;
            this.loadingSubject.next(true);

            return this.ownershipService.releaseAllFlights(currentOwner).pipe(
              map(() => {
                this.refreshSubject.next();
                return true;
              }),
              catchError((err) => {
                return throwError(() => new Error(buildFriendlyError(err)));
              }),
              finalize(() => {
                this.inProgress = false;
                this.loadingSubject.next(false);
              })
            );
          },
        },
      })
      .afterClosed()
      .subscribe();
  }
  resetCfhCompletion(): void {
    this.cfhCompletedSubject = new Subject<void>();
    this.cfhCompleted$ = this.cfhCompletedSubject.asObservable();
  }
}
